import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { api } from '../lib/api';
import { money } from '../hooks/usePageContent';
import { TextArea, SelectField } from './AdminFields';
import RentalFleetAdmin from './AdminRentalFleet';
import RentalCalendarAdmin from './AdminRentalCalendar';
import { toast } from 'sonner';

const DEP_COLORS = { paid: 'text-emerald-700', processing: 'text-amber-700', none: 'text-white/50', pending: 'text-white/50' };

const RentalRow = ({ r, onSaved }) => {
    const [open, setOpen] = useState(false);
    const [f, setF] = useState({ status: r.status, admin_notes: r.admin_notes || '' });
    const save = async () => {
        try { await api.put(`/admin/rentals/${r.id}`, f); toast.success(`${r.request_number} saved`); onSaved(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    };
    return (
        <div className="border-b border-white/10" data-testid={`admin-rental-${r.id}`}>
            <button onClick={() => setOpen(!open)} className="w-full p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-center text-left hover:bg-white/5" data-testid={`admin-rental-toggle-${r.id}`}>
                <span className="font-display font-bold text-white">{r.request_number}</span>
                <span className="text-sm text-white/70 truncate">{r.name}</span>
                <span className="text-sm text-white/70 truncate md:col-span-2">{[r.machine_name, r.course_name].filter(Boolean).join(' + ')}</span>
                <span className="text-sm text-white/50">Starts {r.start_date}</span>
                <span className={`label-caps text-xs ${DEP_COLORS[r.deposit_status] || 'text-red-600'}`} data-testid={`admin-rental-deposit-${r.id}`}>
                    {r.deposit ? `${money(r.deposit)} ${r.deposit_status}` : 'No deposit'} · {r.status}
                </span>
            </button>
            {open && (
                <div className="p-5 bg-brand-bg grid grid-cols-1 md:grid-cols-2 gap-6" data-testid={`admin-rental-details-${r.id}`}>
                    <div className="text-sm text-white/70 space-y-1">
                        {r.date_conflict && <div className="text-red-600 font-bold" data-testid={`admin-rental-conflict-${r.id}`}>Date conflict: deposit paid but another booking holds some of these days — move dates or refund.</div>}
                        <div>{r.name}{r.company ? ` · ${r.company}` : ''}</div>
                        <div><a href={`mailto:${r.email}`} className="text-brand-primary">{r.email}</a> · {r.phone}</div>
                        <div>{r.city}, {r.state} · {r.delivery === 'delivery' ? 'Deliver to site' : 'Customer pickup'}</div>
                        {r.machine_name && <div>{r.duration_count} {r.duration_type === 'weekly' ? 'week(s)' : 'day(s)'} · est. {money(r.estimated_rental)}</div>}
                        {r.course_name && <div>Course: {r.course_name} ({money(r.course_price)})</div>}
                        {r.notes && <div className="pt-2 text-white/50">Notes: {r.notes}</div>}
                    </div>
                    <div className="space-y-3">
                        <SelectField label="Status" value={f.status} onChange={(v) => setF({ ...f, status: v })} testid={`admin-rental-status-${r.id}`}
                            options={[['new', 'New'], ['confirmed', 'Confirmed'], ['completed', 'Completed'], ['cancelled', 'Cancelled']]} />
                        <TextArea label="Internal notes" rows={2} value={f.admin_notes} onChange={(v) => setF({ ...f, admin_notes: v })} testid={`admin-rental-notes-${r.id}`} />
                        <button onClick={save} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps" data-testid={`admin-rental-save-${r.id}`}><Save className="h-4 w-4" /> Save</button>
                    </div>
                </div>
            )}
        </div>
    );
};

const RentalsAdmin = () => {
    const [items, setItems] = useState([]);
    const load = () => api.get('/admin/rentals').then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    return (
        <div data-testid="admin-rentals">
            <RentalCalendarAdmin />
            <RentalFleetAdmin />
            <h2 className="font-display font-black uppercase text-3xl mb-2">Rental requests</h2>
            <p className="text-white/50 text-sm mb-6">Terms and page text live in the Pages tab.</p>
            <div className="border border-white/10" data-testid="admin-rentals-list">
                {items.length === 0 && <div className="p-6 text-white/50">No rental requests yet.</div>}
                {items.map((r) => <RentalRow key={r.id} r={r} onSaved={load} />)}
            </div>
        </div>
    );
};

export default RentalsAdmin;
